import React, { useState,useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import '../../index.css';
import logo from '../../logo.png';

// Authentication check hook
const useAuth = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      
      if (!token) {
        // Redirect to login if no token exists
        navigate('/login');
        return;
      }

      // Optional: Verify token validity with backend
      const verifyToken = async () => {
        try {
          const response = await fetch('https://kiosk-8nabk06g0-sergelens-projects.vercel.app/api/verify-token', {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (!response.ok) {
            // Token is invalid or expired
            localStorage.removeItem('token');
            navigate('/login');
          }
        } catch (error) {
          console.error('Token verification failed:', error);
          localStorage.removeItem('token');
          navigate('/login');
        }
      };

      verifyToken();
    };

    checkAuth();
  }, [navigate]);
};

function Login() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch('https://kiosk-8nabk06g0-sergelens-projects.vercel.app/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Нэвтрэх нэр, нууц үг буруу байна');
      }
      
      // Store the token in localStorage
      localStorage.setItem('token', data.access_token);
      
      // Redirect to home page
      navigate('/home');
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-blue-50 flex flex-col">
      {/* Header */}
      <header className="w-full p-4 flex justify-between items-center">
        <div></div>
        <div className="flex items-center space-x-4">
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-4">
        {/* Logo */}
        <div className="mb-12 text-center">
          <img 
            src={logo}
            alt="DAN System Logo"
            className="mb-4"
            style={{ width: '500px', height: '100px' }}
          />
        </div>

        {/* Login Form */}
        <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-bold text-blue-600 mb-6" style={{color:'#21396c'}}>Нэвтрэх</h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Username field */}
            <div>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Нэвтрэх нэр"
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isLoading}
              />
            </div>

            {/* Password field */}
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••"
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                disabled={isLoading}
              >
                {showPassword ? (
                  <EyeOffIcon className="w-5 h-5" />
                ) : (
                  <EyeIcon className="w-5 h-5" />
                )}
              </button>
            </div>

            {/* Submit button */}
            <button
              type="submit"
              className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              style={{backgroundColor:'#21396c'}}
              disabled={isLoading}
            >
              {isLoading ? 'Нэвтрэх...' : 'Нэвтрэх'}
            </button>

            {error && (
              <p className="text-red-500 text-sm text-center">{error}</p>
            )}
          </form>
        </div>

        {/* Footer */}
        <footer className="w-full mt-12 py-4 text-center text-sm text-gray-600">
          <div className="flex justify-center space-x-4 mb-4">
            <a href="#" className="hover:text-blue-600">Түгээмэл асуулт хариулт</a>
            <a href="#" className="hover:text-blue-600">Ашиглах заавар</a>
            <a href="#" className="hover:text-blue-600">Холбоо барих</a>
          </div>
          <p>© 2024 Бүх эрх хуулиар хамгаалагдсан</p>
        </footer>
      </main>
    </div>
  );
}

export default Login;